import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { buildBotLink, CHANNEL_URL_BY_PLATFORM } from "@/lib/site";
import { trackClick } from "@/lib/track";

// target для аналитики совпадает с LANDING_CLICK_TARGETS на бэкенде (app.ts),
// иначе визит запишется как "bot" и мы не увидим разделения по мессенджерам.
function trackTarget(platform, channel) {
  if (platform === "telegram") return channel ? "telegram_channel" : "telegram_bot";
  return channel ? "channel" : "bot";
}

const TelegramMark = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className="h-[1.15em] w-[1.15em] flex-none">
    <path d="M21.94 4.6 18.9 19.2c-.23 1.02-.84 1.27-1.7.79l-4.7-3.46-2.27 2.18c-.25.25-.46.46-.94.46l.34-4.79 8.72-7.88c.38-.34-.08-.53-.59-.19l-10.78 6.79-4.64-1.45c-1.01-.32-1.03-1.01.21-1.5l18.14-6.99c.84-.31 1.58.2 1.25 1.45Z" />
  </svg>
);

// Не копия фирменного знака MAX, а нейтральная пиктограмма в стиле лендинга.
const MaxMark = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="h-[1.15em] w-[1.15em] flex-none">
    <rect x="2.5" y="2.5" width="19" height="19" rx="6" stroke="currentColor" strokeWidth="1.8" />
    <path d="M7.4 16.2V8.4l4.6 5 4.6-5v7.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PLATFORM_MARKS = { telegram: TelegramMark, max: MaxMark };

// Главный CTA: разбор открывается прямо на сайте (/chat), без установки мессенджера.
export const ChatCTAButton = ({
  goal = "cta_web_chat",
  children,
  className = "",
  showArrow = true,
  testId,
  size = "lg",
}) => {
  const pad = size === "sm" ? "px-5 py-2.5 text-sm" : "px-8 py-4 text-base";
  return (
    <motion.a
      href="/chat"
      data-goal={goal}
      data-testid={testId}
      onClick={() => trackClick("web_chat")}
      whileHover={{ scale: 1.035 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className={`group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-semibold tracking-wide gold-gradient text-[#0B0B1F] glow-gold ${pad} ${className}`}
    >
      {children}
      {showArrow && (
        <ArrowRight className="h-[1.15em] w-[1.15em] transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </motion.a>
  );
};

export const ChannelLink = ({ platform = "max", goal, children, testId, className = "" }) => {
  const href = CHANNEL_URL_BY_PLATFORM[platform];
  return (
    <a
      href={href}
      data-goal={goal}
      data-testid={testId}
      onClick={(e) => {
        e.preventDefault();
        trackClick(trackTarget(platform, true));
        window.open(href, "_blank", "noopener,noreferrer");
      }}
      className={`text-[#EDE6D3]/70 underline underline-offset-4 transition-colors hover:text-[#F0C674] ${className}`}
    >
      {children}
    </a>
  );
};

export const CTAButton = ({
  goal = "cta_max_bot",
  platform = "max",
  channel = false,
  primary,
  children,
  className = "",
  showArrow = true,
  testId,
  size = "lg",
}) => {
  const isPrimary = primary ?? !channel;
  const href = channel ? CHANNEL_URL_BY_PLATFORM[platform] : buildBotLink(platform);

  const handleClick = (e) => {
    e.preventDefault();
    trackClick(trackTarget(platform, channel));
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const pad = size === "sm" ? "px-5 py-2.5 text-sm" : "px-8 py-4 text-base";
  const Mark = channel ? null : PLATFORM_MARKS[platform];
  // Ширина задаётся инлайном, а не Tailwind-классом: index.css здесь заморожен,
  // новых утилит в нём нет (см. lib/site.js рядом). Кнопки мессенджеров должны
  // быть одинаковыми, хотя «В Telegram» длиннее «В MAX».
  const equalWidth = Mark ? { minWidth: size === "sm" ? "10.5rem" : "13rem" } : undefined;

  return (
    <motion.a
      href={href}
      onClick={handleClick}
      data-goal={goal}
      data-testid={testId}
      style={equalWidth}
      whileHover={{ scale: 1.035 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: "spring", stiffness: 400, damping: 22 }}
      className={`group inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full font-semibold tracking-wide ${pad} ${
        isPrimary
          ? "gold-gradient text-[#0B0B1F] glow-gold"
          : "border border-[#D4AF6E]/50 text-[#EDE6D3] hover:border-[#D4AF6E] hover:bg-[#D4AF6E]/10"
      } ${className}`}
    >
      {Mark && <Mark />}
      {children}
      {showArrow && (
        <ArrowRight className="h-[1.15em] w-[1.15em] transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </motion.a>
  );
};
